import Box from "@cloudscape-design/components/box";
import Container from "@cloudscape-design/components/container";
import ContentLayout from "@cloudscape-design/components/content-layout";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Link from "@cloudscape-design/components/link";
import SpaceBetween from "@cloudscape-design/components/space-between";
import TopNavigation from "@cloudscape-design/components/top-navigation";
import { useEffect, type ReactNode } from "react";
import { facebookUrl } from "../../api/client";
import { useFollow } from "../../router/useRoute";

interface Props {
  /** Bắt đầu bằng động từ với trang thao tác, bằng danh từ với trang nội dung. */
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Rút gọn khối hero.
   *
   * Trên điện thoại, hero đầy đủ chiếm 268px trong 812px màn hình đầu tiên. Ở
   * trang đăng nhập thì xứng đáng — đó là lần đầu người dùng gặp sản phẩm. Ở
   * trang chính sách hay điều khoản thì không: người ta mở nó để đọc một nội
   * dung cụ thể, và câu quảng cáo đẩy nội dung đó xuống dưới màn hình.
   */
  hero?: "compact" | "full";
}

/**
 * Hero header là use case Cloudscape cho phép ở trang đăng nhập: giai đoạn đầu
 * hành trình, mật độ thông tin thấp. Console thành viên thì không dùng.
 */
export function PublicShell({ title, description, children, footer, hero = "full" }: Props) {
  const follow = useFollow();

  /*
    Tiêu đề tab theo từng trang.

    Trước đây cả chín trang công khai dùng chung đúng một `<title>` từ
    index.html, nên tab, bookmark và kết quả tìm kiếm không phân biệt được trang
    nào — kể cả người duyệt Chrome Web Store mở trang chính sách cũng chỉ thấy
    "FaceGo Account".
  */
  useEffect(() => {
    document.title = `${title} · FaceGo Account`;
  }, [title]);

  return (
    <>
      <TopNavigation
        identity={{ href: "/login", title: "FaceGo Account", onFollow: follow }}
        // Ở bề ngang hẹp, TopNavigation gộp các tiện ích vào một nút menu. Không
        // truyền i18nStrings thì nút đó không có nhãn nào, trình đọc màn hình
        // đọc ra một nút trống.
        i18nStrings={{
          overflowMenuTriggerText: "Thêm",
          overflowMenuTitleText: "Tất cả",
          overflowMenuBackIconAriaLabel: "Quay lại",
          overflowMenuDismissIconAriaLabel: "Đóng menu",
        }}
        utilities={[
          {
            type: "button",
            text: "Mở Facebook",
            href: facebookUrl,
            external: true,
            externalIconAriaLabel: "Mở trong thẻ mới",
          },
        ]}
      />
      <ContentLayout
        defaultPadding
        headerVariant="high-contrast"
        header={
          <Box padding={{ vertical: hero === "compact" ? "m" : "xl" }}>
            <Grid gridDefinition={[{ colspan: { default: 12, s: 7 } }]}>
              <SpaceBetween size="s">
                {/* `variant="div"` chứ không phải "h1": tiêu đề thật của trang
                    nằm ở thẻ nội dung bên dưới. Để câu quảng cáo làm h1 thì cả
                    chín trang có chung một h1, trình đọc màn hình và bộ máy tìm
                    kiếm đều đọc sai trọng tâm của trang. */}
                <Box variant="div" fontSize={hero === "compact" ? "heading-l" : "display-l"} fontWeight="bold">
                  Một tài khoản cho toàn bộ FaceGo
                </Box>
                {hero === "compact" ? null : (
                  <Box variant="p" fontSize="heading-s" color="text-body-secondary">
                    Quản lý gói cước và quyền sử dụng extension trong cùng một hệ thống.
                  </Box>
                )}
              </SpaceBetween>
            </Grid>
          </Box>
        }
      >
        <Grid
          gridDefinition={[
            { colspan: { default: 12, s: 8, m: 6 }, offset: { s: 2, m: 3 } },
          ]}
        >
          <SpaceBetween size="l">
            <Container header={<Header variant="h1" description={description}>{title}</Header>}>
              {children}
            </Container>
            {footer}
            <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
              <SpaceBetween size="xs">
                <div>© 2026 FaceGo Team</div>
                <SpaceBetween size="l" direction="horizontal">
                  <Link href="/terms" onFollow={follow}>
                    Điều khoản
                  </Link>
                  <Link href="/privacy" onFollow={follow}>
                    Bảo mật
                  </Link>
                  {/* Đường dẫn này đã khai với Chrome Web Store là nơi yêu cầu
                      xoá dữ liệu; không có lối vào thì chỉ ai biết sẵn URL mới
                      tới được. */}
                  <Link href="/account/delete" onFollow={follow}>
                    Xoá tài khoản
                  </Link>
                  <Link href="/support" onFollow={follow}>
                    Hỗ trợ
                  </Link>
                </SpaceBetween>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </Grid>
      </ContentLayout>
    </>
  );
}
